import type { ApiHost } from './config';

/**
 * Ported from the legacy `SportsApp.Routes` catalogue. Each entry pairs a host with
 * a path so callers never assemble URLs, and so a host move is a one-line change.
 *
 * The legacy `LocalApi` routes are deliberately absent: they were ASP.NET
 * controllers in the old host, which this app does not have.
 */
export interface Endpoint {
  host: ApiHost;
  path: string;
}

const sports = (path: string): Endpoint => ({ host: 'sports', path });
/** Classic SportsAPI (`sAPI`) — use only while a route is missing from SportsAPI2. */
const sportsLegacy = (path: string): Endpoint => ({ host: 'sportsLegacy', path });
const users = (path: string): Endpoint => ({ host: 'users', path });
const wallet = (path: string): Endpoint => ({ host: 'wallet', path });
const walletcore = (path: string): Endpoint => ({ host: 'walletcore', path });
const casino = (path: string): Endpoint => ({ host: 'casino', path });
const translate = (path: string): Endpoint => ({ host: 'translate', path });
const transactions = (path: string): Endpoint => ({ host: 'transactions', path });
const customBets = (path: string): Endpoint => ({ host: 'customBets', path });

export const endpoints = {
  sports: {
    menuFull: sports('api/v1/front/menu/full'),
    search: sports('api/v1/front/search'),
    eventsTournament: sports('api/v1/front/events/tournament'),
    eventsCategory: sports('api/v1/front/events/category'),
    eventDetails: (eventId: string | number): Endpoint =>
      sports(`api/v1/front/events/${eventId}`),
    /** Prematch Bet Builder markets — same payload/response as details, markets filtered. */
    betBuilder: (eventId: string | number): Endpoint =>
      sports(`api/v1/front/events/${eventId}/betbuilder`),
    topEvents: sports('api/v1/front/events/topevents'),
    lastMinute: sports('api/v1/front/events/lastminute'),
    dateRange: sports('api/v1/front/events/daterange'),
    /**
     * My Bets expand — seed inline stats.
     * TEMP: classic SportsAPI until SportsAPI2 implements `api/v1/front/events/results`
     * (then switch to `sports('api/v1/front/events/results')`).
     */
    eventResults: sportsLegacy('api/front/events/results'),
    /**
     * Tournament outrights (seasons).
     * TEMP: classic SportsAPI until SportsAPI2 implements `api/v1/front/seasons`
     * (then switch to `sports('api/v1/front/seasons')`).
     */
    seasons: sportsLegacy('api/front/seasons'),
  },

  live: {
    sports: sports('api/v1/front/live/sports'),
    tables: sports('api/v1/front/live/tables'),
    tournaments: sports('api/v1/front/live/tournaments'),
    eventDetails: (eventId: string | number): Endpoint =>
      sports(`api/v1/front/live/${eventId}`),
  },

  betting: {
    /** Reuse-ticket odds refresh — not yet migrated to SportsAPI2. */
    refresh: sports('api/betting/refresh'),
    cashoutList: sports('api/v1/betting/cashoutlist'),
    /**
     * My Bets open stats widgets.
     * TEMP: classic SportsAPI until SportsAPI2 implements `api/v1/betting/statistics`
     * (then switch to `sports('api/v1/betting/statistics')`).
     */
    statistics: sportsLegacy('api/betting/statistics'),
  },

  player: {
    logout: users('sb/api/common/logout'),
    register: users('sb/api/common/register'),
    registerBonuses: users('sb/api/common/registerbonuses'),
    unique: users('sb/api/common/unique'),
    forgotPassword: users('sb/api/common/forgotpassword'),
    forgotPasswordSms: users('sb/api/common/forgotpasswordsms'),
    forgotUsername: users('sb/api/common/forgotusername'),
    resetPassword: users('sb/api/common/resetpassword'),
    checkSession: users('sb/api/common/checksession'),
    verifySms: users('sb/api/verification/sms/verify'),
    resendSms: users('sb/api/verification/sms/resend'),
    verifyEmail: users('sb/api/player/verifyemail'),
    details: users('sb/api/player/profile/details'),
    balanceBreakdown: users('sb/api/player/profile/balancebreakdown'),
    refreshCredits: users('sb/api/player/profile/refreshcredits'),
    activity: users('sb/api/player/profile/activity'),
    transactions: users('sb/api/player/profile/transactions'),
    bonusTransactions: users('sb/api/player/profile/bonustransactions'),
    bonusTransactionDetails: users('sb/api/player/profile/bonustransactionsdetails'),
    depositHistory: users('sb/api/player/profile/deposits'),
    depositLimits: users('sb/api/player/profile/depositlimits'),
    depositBonuses: users('sb/api/player/profile/depositbonuses'),
    depositCancel: users('sb/api/player/deposits/usercancel'),
    withdrawalHistory: users('sb/api/player/profile/withdrawals'),
    lostBonuses: users('sb/api/player/profile/lostbonuses'),
    lostBonusConfirm: users('sb/api/player/profile/lostbonuscreate'),
    selfExclude: users('sb/api/player/profile/exclude'),
    revokeExclusion: users('sb/api/player/profile/revoke'),
    setTimezone: users('sb/api/player/profile/timezone'),
    setLanguage: users('sb/api/player/profile/language'),
    setThemeColor: users('sb/api/player/profile/themecolor'),
    editDetails: users('sb/api/player/edit/details'),
    editPersonal: users('sb/api/player/edit/personal'),
    editContact: users('sb/api/player/edit/contact'),
  },

  tickets: {
    list: users('sb/api/tickets/list'),
    details: users('sb/api/tickets/details'),
    count: users('sb/api/tickets/count'),
    reuse: users('sb/api/tickets/reuse'),
    cashout: users('sb/api/tickets/cashoutnew'),
    create: users('sb/api/tickets/create'),
    save: users('sb/api/tickets/save'),
    payout: users('sb/api/ticketactions/v1/payout'),
    creditorList: users('sb/api/tickets/creditorlist'),
    creditBet: users('sb/api/creditbet/create'),
    betzone: users('sb/api/tickets/betzone'),
    bookDetails: users('sb/api/book/details'),
    bookSave: users('sb/api/book/save'),
  },

  cms: {
    pages: users('sb/api/pages/list'),
    page: users('sb/api/pages/details'),
    contactForm: users('sb/api/common/email'),
    highlightsSetting: users('sb/api/highlights/setting'),
  },

  messages: {
    list: users('sb/api/player/messages/list'),
    details: users('sb/api/player/messages/details'),
  },

  betzone: {
    list: users('sb/api/betzone/list'),
  },

  providers: {
    payment: users('sb/api/providers/payment/list'),
    deposits: users('sb/api/providers/deposits/list'),
    withdrawals: users('sb/api/providers/withdrawals/list'),
    withdrawalRequest: users('sb/api/player/withdrawals/save'),
    banks: users('sb/api/providers/payment/banks'),
    paymentAccountList: users('sb/api/player/providers/list'),
    paymentAccounts: users('sb/api/player/providers/paymentaccounts'),
    depositProvider: users('sb/api/sportsbook/deposits/provider'),
  },

  wallet: {
    deposit: (provider: string): Endpoint => wallet(`api/payments/${provider}/deposit`),
    withdrawal: (provider: string): Endpoint => wallet(`api/payments/${provider}/withdrawal`),
    providers: (provider: string): Endpoint => wallet(`api/payments/${provider}/providers`),
    banks: (provider: string): Endpoint => wallet(`api/payments/${provider}/banks`),
    walletTypes: (provider: string): Endpoint => wallet(`api/payments/${provider}/wallettypes`),
    gameSession: (provider: string): Endpoint => wallet(`api/games/${provider}/session`),
    maksiParaBanks: wallet('api/payments/maksipara/banks'),
  },

  walletcore: {
    gameSession: (provider: string): Endpoint => walletcore(`api/games/${provider}/session`),
  },

  casino: {
    vendors: casino('api/v1/games/vendors'),
    games: casino('api/v1/games/list'),
    categories: users('sb/api/games/categories'),
    addFavorite: users('sb/api/player/favoritegame/add'),
    removeFavorite: users('sb/api/player/favoritegame/remove'),
  },

  translations: {
    all: translate('api/v1/translates/all'),
  },

  tickers: {
    latestCasinoTransactions: transactions('api/v1/latestcasinotransactions/list'),
    biggestCasinoWins: transactions('api/v1/casinotransactionsstatistics/biggestwinnings'),
  },

  customBets: {
    calculateFilter: customBets('api/v1/custom-bets/calculate-filter'),
  },
} as const;
